import { IHttpResponse } from 'angular';
import { gpCommon } from '../app';

interface EmailAndToken {
    email: string;
    token: string;
}


interface Jwt {
    header: {
        typ: string;
        alg: string;
    };
    payload: {
        iss: string;
        aud: string;
        exp: number;
        nbf: number;
        nameid?: string;
        unique_name?: string;
    };
    signature: string;
}


export type AuthCheckResultChoices = 'authorized' | 'unauthorized' | 'notsure';

export interface IAuthorisationCheckerStatus {
    email: string;
    userId: string;
    token: string;
    loggedInStatus: AuthCheckResultChoices;
}

export class AuthorisationCheckerService {
    public static readonly serviceName = "gpAuthorisationChecker"
    static $inject = ['$http', '$q'];
    private serviceBase = __gameplan_service_url || 'http://localhost/';


    constructor(
        private readonly $http: ng.IHttpService,
        private readonly $q: ng.IQService,
    ) {
    }

    public checkAuthorisation(authData: EmailAndToken): ng.IPromise<IAuthorisationCheckerStatus> {

        let $q = this.$q;

        let deferred = $q.defer<IAuthorisationCheckerStatus>();
        deferred.promise.catch(reason => {
            // catch this here just so errors in the console appear about unhandled rejections
        });
        let jwt = parseJwtToken(authData.token);
        const userId = (jwt ? jwt.payload.nameid : "") || "";

        if (jwt && !this.hasTokenExpired(jwt) && userId) {

            this.checkAuthorisationWithServerNoRejection(authData)
                .then(authCheckResult => {

                    // if there was a network error, or some other strange thing going on, we want to keep using our token
                    // if it is valid.
                    //
                    // Only if the server has said "No, you are not authorized any more" of if the token expires do we want to 
                    // say the login was not accepted.
                    if (authCheckResult === 'unauthorized') {
                        deferred.reject(authCheckResult);
                    } else {
                        deferred.resolve({
                            email: authData.email,
                            loggedInStatus: authCheckResult,
                            token: authData.token,
                            userId: userId,
                        });
                    }
                });
        } else {
            deferred.reject("Invalid token");
        }

        return deferred.promise;
    }

    // Sends a request to the server to check if our token is actually accepted or not
    private checkAuthorisationWithServerNoRejection(authData: EmailAndToken): ng.IPromise<AuthCheckResultChoices> {

        const deferred = this.$q.defer<AuthCheckResultChoices>();
        deferred.promise.catch(err => {
            // catch this here just so errors in the console appear about unhandled rejections
        });

        if (authData && authData.token) {
            const req = this.$http.post<void>(
                this.serviceBase + 'api/accounts/IsAuthorized',
                null,
                { headers: { 'Authorization': 'bearer ' + authData.token, } }
            );

            req
                .then(response => {
                    deferred.resolve('authorized');
                })
                .catch((response: IHttpResponse<void>) => {

                    let resultOfCheck: AuthCheckResultChoices;
                    let isAuthorized = false;
                    if (response.xhrStatus === 'error' || response.xhrStatus === 'timeout' || response.xhrStatus === 'abort') {
                        resultOfCheck = 'notsure';
                    } else {
                        switch (response.status) {
                            case 401: resultOfCheck = 'unauthorized'; break;
                            case 402: resultOfCheck = 'unauthorized'; break;
                            case 403: resultOfCheck = 'unauthorized'; break;
                            case 418: resultOfCheck = 'unauthorized'; break;  // we don't want teapots

                            default: {
                                const jwt = parseJwtToken(authData.token);

                                if (jwt && this.hasTokenExpired(jwt)) {
                                    resultOfCheck = 'unauthorized';
                                } else {
                                    resultOfCheck = 'notsure';
                                }
                            }
                        }
                    }

                    deferred.resolve(resultOfCheck);

                });
        } else {
            deferred.resolve('unauthorized');
        }

        return deferred.promise;

    }

    private hasTokenExpired(token: Jwt | null | undefined) {
        let now = Date.now().valueOf() / 1000;
        return token && token.payload.exp && token.payload.exp <= now;
    }
}

gpCommon.service(AuthorisationCheckerService.serviceName, AuthorisationCheckerService);

export function parseJwtToken(jwt: string) {
    try {
        let jwtParts = jwt.split('.');

        if ((jwtParts.length === 3)) {
            let jwt: Jwt = {
                header: JSON.parse(window.atob(jwtParts[0])),
                payload: JSON.parse(window.atob(jwtParts[1])),
                signature: jwtParts[2]
            };

            return jwt;
        }
    } catch (error) {
        //ignore
    }

    return null;
}
